import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import splinterlandsSettingsService from '../services/SplinterlandsSettingsService';

// Create context for the item shopping cart
const ItemCartContext = createContext();

// Custom hook to easily use the ItemCartContext
export const useItemCart = () => useContext(ItemCartContext);

// Local storage key for persistent item cart items
const ITEM_CART_STORAGE_KEY = 'splinterlands_item_cart_items';

/**
 * Fetches the latest Splinterlands blockchain settings
 * This helps ensure transactions are properly synchronized with the blockchain
 * Uses cached settings service to avoid redundant API calls
 * @returns {Promise<Object>} Latest blockchain settings
 */
const getLatestBlockchainSettings = async () => {
  try {
    // Use the cached settings service instead of direct API call
    const settings = await splinterlandsSettingsService.getSettings();
    return settings;
  } catch (error) {
    console.error('Error fetching blockchain settings:', error);
    return null;
  }
};

/**
 * Gets the current DEC price from PriceService
 * Used for market purchases to ensure consistent pricing
 * @returns {Promise<number>} Current DEC price in USD
 */
const getCurrentDecPrice = async () => {
  try {
    // Try to get price from local PriceService first
    const priceService = (await import('../services/PriceService')).default;
    const { prices } = priceService.getData();
    
    if (prices && prices.dec && prices.dec > 0) {
      return Number(prices.dec);
    }
    
    // Fallback to fetching from Hive Engine directly
    const response = await axios.get(
      'https://api.hive-engine.com/rpc/contracts', 
      {
        method: 'POST',
        data: {
          jsonrpc: '2.0',
          id: 1,
          method: 'findOne',
          params: {
            contract: 'market',
            table: 'metrics',
            query: { symbol: 'DEC' }
          }
        }
      }
    );
    
    if (response.data && response.data.result) {
      return Number(response.data.result.lastPrice);
    }
    
    return 0.001; // Fallback default if all else fails
  } catch (error) {
    console.error('Error getting current DEC price:', error);
    return 0.001; // Default fallback price
  }
};

/**
 * Gets the current CREDITS price
 * @returns {Promise<number>} Current CREDITS price in USD
 */
const getCreditsPrice = async () => {
  // Credits are typically priced at a fixed rate of $1 = 1000 CREDITS
  return 0.001; // $0.001 per credit
};

/**
 * Resolves a listing's price in a specific currency. The market API returns the listing's
 * native price (usually USD) plus an `otherCurrencies` array with the actual DEC/CREDITS
 * amounts as priced by Splinterlands at listing time - use that instead of converting the
 * USD price ourselves, since a self-computed conversion can drift from what the backend
 * actually expects and gets rejected as "Estimated cost too low".
 * @param {Object} listing - A listing with { price, currency, otherCurrencies }
 * @param {string} currency - Target currency ('DEC' or 'CREDITS')
 * @returns {number}
 */
const getPriceInCurrency = (listing, currency) => {
  if (!listing) return 0;
  if (listing.currency === currency) return listing.price;
  const match = (listing.otherCurrencies || []).find(c => c.currency === currency);
  return match ? parseFloat(match.price) : listing.price;
};

/**
 * ItemCartProvider component that provides global state for the item shopping cart
 * @param {object} props - React props with children
 * @returns {JSX.Element} - ItemCartProvider element
 */
export const ItemCartProvider = ({ children }) => {
  // State for item cart items
  const [itemCartItems, setItemCartItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load item cart items from localStorage on first render
  useEffect(() => {
    const savedItems = localStorage.getItem(ITEM_CART_STORAGE_KEY);
    if (savedItems) {
      try {
        const parsedItems = JSON.parse(savedItems);
        if (Array.isArray(parsedItems)) {
          setItemCartItems(parsedItems);
        }
      } catch (error) {
        console.error('Error parsing saved item cart items:', error);
        localStorage.removeItem(ITEM_CART_STORAGE_KEY);
      }
    }
  }, []);

  // Save item cart items to localStorage on every change
  useEffect(() => {
    localStorage.setItem(ITEM_CART_STORAGE_KEY, JSON.stringify(itemCartItems));
  }, [itemCartItems]);

  /**
   * Fetches the current raw listings for an item from the Splinterlands marketplace API,
   * sorted by price ascending. No selection/quantity-fulfillment logic - just the live data.
   * @param {Object} item - The item data
   * @returns {Promise<Array>} - All current listings for the item
   */
  const fetchRawItemListings = async (item) => {
    try {
      // The API wants assetName/detailId exactly as returned by the market landing endpoint
      // (e.g. "PACKS", "TOTEMS") - no singularization or other mapping needed. Sending the
      // guessed singular form ("PACK", "TOTEM", ...) gets rejected with 400 "Invalid assetName".
      const payload = {
        assetName: item.assetName,
        detailIds: item.detailId ? [item.detailId] : [],
        sort: {
          field: "price",
          order: "asc"
        }
      };

      const response = await axios.post('https://vapi.splinterlands.com/market/listing-items', payload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.data && response.data.status === 'success' && response.data.data && Array.isArray(response.data.data.items)) {
        const apiItems = response.data.data.items;
        
        const listings = apiItems.map((listing, index) => ({
          // listingId and listingItemId are two distinct fields the API always returns -
          // the purchase payload needs listingItemId specifically, not the general listingId.
          listingItemId: listing.listingItemId,
          listingId: listing.listingId,
          price: parseFloat(listing.price || 0),
          quantity: parseInt(listing.quantityRemaining || listing.quantity || 1),
          seller: listing.player || 'Unknown',
          currency: listing.currency || 'DEC',
          detailId: listing.detailId,
          itemId: listing.itemId,
          otherCurrencies: listing.otherCurrencies || [],
          createdDate: listing.createdDate,
          rawData: listing
        }));

        return listings.sort((a, b) => a.price - b.price);
      }

      return [];
    } catch (error) {
      console.error('Error fetching item listings:', error);
      return [];
    }
  };

  /**
   * Fetches current listings and auto-selects the cheapest ones to fulfill a quantity.
   * @param {Object} item - The item data
   * @param {number} quantity - Desired quantity
   * @returns {Promise<Array>} - Array of listings (with selectedQuantity/estimatedCost) that fulfill the quantity
   */
  const fetchItemListings = async (item, quantity = 1) => {
    const sortedListings = await fetchRawItemListings(item);
    const selectedListings = [];
    let remaining = quantity;

    for (const listing of sortedListings) {
      if (remaining <= 0) break;

      const takeQuantity = Math.min(remaining, listing.quantity);
      selectedListings.push({
        ...listing,
        selectedQuantity: takeQuantity,
        estimatedCost: listing.price * takeQuantity
      });

      remaining -= takeQuantity;
    }

    return selectedListings;
  };

  /**
   * Adds an item to the shopping cart
   * @param {Object} item - The item data to add
   * @param {number} quantity - Quantity to add (default: 1)
   * @param {Array|null} explicitListings - Optional pre-selected listings (e.g. the user picked a
   *   specific seller/row in ItemDetailsDialog instead of the auto-selected cheapest ones). Each
   *   entry needs at least { listingItemId, selectedQuantity, price, currency }. When omitted, the
   *   cheapest available listings are auto-selected to fulfill the quantity (previous behavior).
   */
  const addToItemCart = async (item, quantity = 1, explicitListings = null) => {
    setLoading(true);

    try {
      const listings = explicitListings && explicitListings.length > 0
        ? explicitListings.map(listing => ({
            ...listing,
            estimatedCost: listing.selectedPrice ?? (listing.price * listing.selectedQuantity)
          }))
        : await fetchItemListings(item, quantity);

      if (listings.length === 0) {
        throw new Error('No listings available for this item');
      }

      const totalCost = listings.reduce((sum, listing) => sum + listing.estimatedCost, 0);
      const totalQuantity = listings.reduce((sum, listing) => sum + listing.selectedQuantity, 0);

      const cartItem = {
        id: `${item.assetName}_${item.detailId}_${Date.now()}`,
        item: item,
        quantity: totalQuantity,
        listings: listings,
        totalCost: totalCost,
        currency: listings[0].currency || 'DEC',
        addedAt: new Date().toISOString()
      };

      setItemCartItems(prevItems => [...prevItems, cartItem]);
    } catch (error) {
      console.error('Error adding item to cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Removes an item from the shopping cart
   * @param {string} cartItemId - The ID of the cart item to remove
   */
  const removeFromItemCart = (cartItemId) => {
    setItemCartItems(prevItems => prevItems.filter(item => item.id !== cartItemId));
  };

  /**
   * Clears the entire item shopping cart
   */
  const clearItemCart = () => {
    setItemCartItems([]);
  };

  /**
   * Purchases a single item
   * @param {Object} cartItem - The cart item to purchase
   * @param {string} currency - The currency for the purchase ('DEC' or 'CREDITS')
   */
  const buyItem = async (cartItem, currency) => {
    return await buyItems([cartItem], currency);
  };

  /**
   * Purchases multiple items in a single transaction
   * @param {Array} cartItems - Array of cart items to purchase
   * @param {string} currency - The currency for the purchase ('DEC' or 'CREDITS')
   */
  const buyItems = async (cartItems, currency) => {
    try {
      setLoading(true);

      // Get the latest blockchain settings
      const blockchainSettings = await getLatestBlockchainSettings();
      if (!blockchainSettings) {
        throw new Error('Failed to fetch blockchain settings');
      }

      // Prepare items for purchase
      const purchaseItems = [];

      for (const cartItem of cartItems) {
        // Refresh live listings for this item (fresh raw data, not the cheapest auto-pick -
        // we want to keep the user's originally chosen seller(s) if they're still available).
        const freshListings = await fetchRawItemListings(cartItem.item);

        if (freshListings.length === 0) {
          throw new Error(`No listings available for ${cartItem.item.detailName}`);
        }

        const freshById = new Map(freshListings.map(l => [l.listingItemId, l]));
        const usedIds = new Set();
        const finalSelections = [];
        let remaining = cartItem.quantity;

        // Prefer the specific listings the user originally selected, capped to whatever
        // quantity is still actually available on each one.
        for (const original of cartItem.listings) {
          if (remaining <= 0) break;
          const fresh = freshById.get(original.listingItemId);
          if (!fresh || fresh.quantity <= 0) continue;

          const takeQuantity = Math.min(remaining, fresh.quantity, original.selectedQuantity);
          finalSelections.push({ listingItemId: fresh.listingItemId, quantity: takeQuantity, price: getPriceInCurrency(fresh, currency) });
          usedIds.add(fresh.listingItemId);
          remaining -= takeQuantity;
        }

        // If the originally chosen listings no longer cover the full quantity (sold out /
        // reduced stock), fall back to the next-cheapest remaining listings to make up the rest.
        if (remaining > 0) {
          for (const fresh of freshListings) {
            if (remaining <= 0) break;
            if (usedIds.has(fresh.listingItemId) || fresh.quantity <= 0) continue;

            const takeQuantity = Math.min(remaining, fresh.quantity);
            finalSelections.push({ listingItemId: fresh.listingItemId, quantity: takeQuantity, price: getPriceInCurrency(fresh, currency) });
            usedIds.add(fresh.listingItemId);
            remaining -= takeQuantity;
          }
        }

        if (finalSelections.length === 0) {
          throw new Error(`No listings available for ${cartItem.item.detailName}`);
        }

        for (const selection of finalSelections) {
          // Splinterlands' own client pads DEC estimatedCost by 1.5% over the base price
          // (confirmed against a real successful tx: base 924.588 DEC -> estimatedCost
          // 938.457 DEC, i.e. base * 1.015) to absorb DEC price movement between quoting
          // and execution; CREDITS has a fixed peg so no buffer is needed there.
          const buffer = currency === 'DEC' ? 1.015 : 1;
          purchaseItems.push({
            listingItemId: selection.listingItemId,
            quantity: selection.quantity,
            currency: currency,
            estimatedCost: Math.round(selection.price * selection.quantity * buffer * 1000) / 1000
          });
        }
      }

      // Generate a unique nonce for this transaction
      const nonce = 'sm' + Math.random().toString(36).substring(2, 15);

      // Prepare the transaction payload. id must be "sm_marketplace_purchase" - Splinterlands'
      // backend only processes ops under that exact id; a mismatched id broadcasts fine to Hive
      // but is never picked up, so nothing gets purchased and there's no error either.
      // Third-party markets (verified against a real peakmonsters tx buying the same kind of
      // item) additionally declare "market" plus "expected_dec_price"/"dec_mismatch_percent"
      // for DEC purchases - the official Splinterlands client's own payload omits all three
      // since it doesn't need to self-identify. Include them for parity with other 3rd-party
      // markets since we are one too.
      const jsonBody = {
        items: purchaseItems,
        market: "elmo.market",
        app: "elmo/1.0.0",
        n: nonce
      };
      if (currency === 'DEC') {
        const decPriceUsd = await getCurrentDecPrice();
        jsonBody.expected_dec_price = decPriceUsd;
        jsonBody.dec_mismatch_percent = 1.5;
      }

      const transactionData = {
        id: 'sm_marketplace_purchase',
        json: JSON.stringify(jsonBody),
        required_auths: [], // Will be filled by Keychain
        required_posting_auths: []
      };

      console.log('Transaction data:', transactionData);

      // Check if Hive Keychain is available
      if (!window.hive_keychain) {
        throw new Error('Hive Keychain not found. Please install the Hive Keychain extension.');
      }

      // Execute the transaction using Hive Keychain
      return new Promise((resolve, reject) => {
        window.hive_keychain.requestCustomJson(
          null, // Let user choose account
          'sm_marketplace_purchase',
          'Active',
          transactionData.json,
          `Purchase ${purchaseItems.length} item(s)`,
          (response) => {
            if (response.success) {
              console.log('Purchase successful:', response);
              
              // Remove purchased items from cart
              const purchasedCartItemIds = cartItems.map(item => item.id);
              setItemCartItems(prevItems => 
                prevItems.filter(item => !purchasedCartItemIds.includes(item.id))
              );
              
              resolve(response);
            } else {
              console.error('Purchase failed:', response);
              reject(new Error(response.message || 'Transaction failed'));
            }
          }
        );
      });

    } catch (error) {
      console.error('Error purchasing items:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // The value provided to the context
  const contextValue = {
    itemCartItems,
    addToItemCart,
    removeFromItemCart,
    clearItemCart,
    buyItem,
    buyItems,
    loading
  };

  return (
    <ItemCartContext.Provider value={contextValue}>
      {children}
    </ItemCartContext.Provider>
  );
};

export default ItemCartContext;
